import React, { useId, useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SortIcon from '@mui/icons-material/Sort';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

import { FilterToolbarButton } from './FilterToolbarButton';

export type SortMenuOption<TValue extends string = string> = {
  label: string;
  value: TValue;
};

export type SortMenuButtonProps<TValue extends string = string> = {
  options: SortMenuOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
};

/** Compact sort menu designed to pair directly with FilterBar's trigger. */
export const SortMenuButton = <TValue extends string>({
  options,
  value,
  onChange,
}: SortMenuButtonProps<TValue>) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const menuId = useId();
  const selected = options.find(option => option.value === value) ?? options[0];
  const open = Boolean(anchor);

  return (
    <>
      <FilterToolbarButton
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={event => setAnchor(event.currentTarget)}
        startIcon={<SortIcon />}
      >
        Sort: {selected?.label ?? 'Choose order'}
      </FilterToolbarButton>
      <Menu
        anchorEl={anchor}
        id={menuId}
        open={open}
        slotProps={{
          list: { 'aria-label': 'Sort claims' },
          paper: {
            sx: {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.14)',
              minWidth: Math.max(anchor?.offsetWidth ?? 0, 220),
              mt: 0.5,
            },
          },
        }}
        onClose={() => setAnchor(null)}
      >
        {options.map(option => {
          const isSelected = option.value === value;
          return (
            <MenuItem
              key={option.value}
              selected={isSelected}
              onClick={() => {
                onChange(option.value);
                setAnchor(null);
              }}
            >
              <ListItemText>{option.label}</ListItemText>
              <ListItemIcon sx={{ justifyContent: 'flex-end', minWidth: 28 }}>
                {isSelected && <CheckRoundedIcon color="primary" fontSize="small" />}
              </ListItemIcon>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
